import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import './index.css';

const Projects = () => {
    const [projects, setProjects] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(1); // Start with center card in view

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                setIsLoading(true);
                const res = await fetch('/api/projects');
                if (!res.ok) {
                    throw new Error(`Failed to fetch projects: ${res.status}`);
                }
                const data = await res.json();
                setProjects(data || []);
            } catch (err) {
                setError(err.message || 'Unknown error');
            } finally {
                setIsLoading(false);
            }
        };
        fetchProjects();
    }, []);

    if (isLoading) {
        return (
            <div className="text-white flex justify-center items-center" style={{ minHeight: '60vh' }}>
                <h2>Loading projects…</h2>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-white flex justify-center items-center" style={{ minHeight: '60vh' }}>
                <h2>Error: {error}</h2>
            </div>
        );
    }

    if (!projects.length) {
        return (
            <div className="text-white flex justify-center items-center" style={{ minHeight: '60vh' }}>
                <h2>No projects found.</h2>
            </div>
        );
    }

    const goToPrevious = () => {
        setCurrentIndex((prev) => {
            if (prev === 0) return projects.length - 1; // Wrap to end
            return prev - 1;
        });
    };

    const goToNext = () => {
        setCurrentIndex((prev) => {
            if (prev === projects.length - 1) return 0; // Wrap to beginning
            return prev + 1;
        });
    };

    // Calculate which cards to show (3 at a time) with wrapping
    const getVisibleProjects = () => {
        const result = [];
        for (let offset = -1; offset <= 1; offset++) {
            let i = currentIndex + offset;
            // Wrap around for infinite carousel
            if (i < 0) i = projects.length + i;
            if (i >= projects.length) i = i - projects.length;
            
            result.push({ 
                project: projects[i], 
                index: i, 
                isCenter: offset === 0,
                offset: offset
            });
        }
        return result;
    };

    const visibleProjects = getVisibleProjects();

    return (
        <div className="projects-carousel bg-color-dark text-white px-24">
            <div className="mx-auto" style={{ maxWidth: 1200 }}>
                <h1 className="text-center text-48px mb-24">Our Projects</h1>
                <p className="text-center text-18px mb-48 mx-auto" style={{ maxWidth: 640 }}>
                    Explore our portfolio of full-stack systems and products.
                </p>

                <div className="carousel-track">
                    {visibleProjects.map(({ project, index, isCenter }) => (
                        <div
                            key={project.project_id}
                            className={`project-card ${isCenter ? 'center' : ''}`}
                            onClick={() => setCurrentIndex(index)}
                        >
                            {project.imageUrl && (
                                <img
                                    src={project.imageUrl}
                                    alt={project.title}
                                />
                            )}
                            <h3>{project.title}</h3>
                            {project.tagline && <p className="tagline">{project.tagline}</p>}
                            {project.status && <span className="status">{project.status}</span>}
                            
                            <div className="project-card-actions">
                                <Link to={`/projects/${project.slug}`} className="btn-primary">
                                    View Details
                                </Link>
                                {project.projectUrl && (
                                    <a
                                        href={project.projectUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="btn-secondary"
                                    >
                                        Visit
                                    </a>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="carousel-nav">
                    <button
                        onClick={goToPrevious}
                        aria-label="Previous project"
                    >
                        ‹
                    </button>
                    <button
                        onClick={goToNext}
                        aria-label="Next project"
                    >
                        ›
                    </button>
                </div>

                <div className="text-center mt-24">
                    <p className="text-14px" style={{ color: 'var(--primary-color-dark)' }}>
                        {currentIndex + 1} of {projects.length}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Projects;
